import React, { useMemo } from 'react';
import { Box, TextField, Typography } from '@mui/material';

/**
 * Structured editor for a US address (mailing or physical-risk), bound to the backend's per-component
 * review fields (addressLine1 / addressLine2 / city / state / zip, or the risk-address equivalents).
 * Each input is seeded from its own field's `value` (the backend already parses a legacy combined line
 * into components as a fallback) and emits `onChange(fieldKey, value)` so the existing per-field save
 * persists each component to its own column — no client-side parse/join. Light validation mirrors the
 * server (State 2 letters, City <= 30, line <= 50).
 */
export interface AddressComponent {
    /** The review field key this input maps to (e.g. "addressLine1" or "riskCity"). */
    key: string;
    value: string;
}

interface Props {
    label: string;
    line1?: AddressComponent;
    line2?: AddressComponent;
    city?: AddressComponent;
    state?: AddressComponent;
    zip?: AddressComponent;
    onChange: (fieldKey: string, value: string) => void;
    /** Required-but-empty → red treatment on line 1. */
    missing?: boolean;
}

export function AddressFieldsEditor({ label, line1, line2, city, state, zip, onChange, missing }: Props) {
    const line1Empty = !line1?.value || line1.value.trim() === '';
    const borderColor = missing && line1Empty ? '#d32f2f' : '#2099d2';
    const bg = missing && line1Empty ? 'rgba(211,47,47,0.06)' : '#fff';
    const fieldSx = useMemo(() => ({
        '& .MuiInputBase-input': { fontSize: '14px', padding: '6px 9px' },
        '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: bg },
        '& .MuiOutlinedInput-notchedOutline': { borderColor },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor },
    }), [borderColor, bg]);

    // Nothing to edit if the backend didn't send any component for this address.
    if (!line1 && !line2 && !city && !state && !zip) return null;

    return (
        <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#5b6770', mb: 0.5 }}>{label}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {line1 && (
                    <TextField fullWidth size="small" placeholder="Address line 1" value={line1.value}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) => onChange(line1.key, e.target.value)} sx={fieldSx} />
                )}
                {line2 && (
                    <TextField fullWidth size="small" placeholder="Apt / Suite / Unit (optional)" value={line2.value}
                        inputProps={{ maxLength: 50 }}
                        onChange={(e) => onChange(line2.key, e.target.value)} sx={fieldSx} />
                )}
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                    {city && (
                        <TextField size="small" placeholder="City" value={city.value}
                            inputProps={{ maxLength: 30 }}
                            onChange={(e) => onChange(city.key, e.target.value)} sx={{ ...fieldSx, flex: 2 }} />
                    )}
                    {state && (
                        <TextField size="small" placeholder="ST" value={state.value}
                            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                            onChange={(e) => onChange(state.key, e.target.value.toUpperCase())} sx={{ ...fieldSx, flex: '0 0 64px' }} />
                    )}
                    {zip && (
                        <TextField size="small" placeholder="ZIP" value={zip.value}
                            inputProps={{ maxLength: 10 }}
                            onChange={(e) => onChange(zip.key, e.target.value)} sx={{ ...fieldSx, flex: '0 0 110px' }} />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default AddressFieldsEditor;
